import {
  DEVICE_CODE_PROMPT_CHANNEL,
  RUNTIME_DOWNLOAD_PROGRESS_CHANNEL,
  type RuntimeDownloadProgress,
  SETUP_PROGRESS_CHANNEL,
} from '@electron/shared/runtime-download.types';

import { ElectronAPI } from '../base/ElectronAPI';
import { type ElectronUnsubscribe } from '../base/types';

import type { LocalPathCheckResult } from '@electron/shared/local-mcp.types';
import type { MailAccountsResult } from '@electron/shared/mail-accounts.types';
import type { OutlookSignInState } from '@electron/shared/outlook-sign-in.types';
import type { CalendarListResult } from '@littlebird/apple-calendar/contract';
// Keep these type-only: the renderer must never bundle the node-side package.
import type {
  ConnectionTestResult,
  DeviceCodeSignInPrompt,
  ServerEntry,
  ServerEntryInput,
  ServerTransport,
  SetupProgressEvent,
} from '@littlebird/axon-mcp';

export type {
  DeviceCodeSignInPrompt,
  SetupProgressEvent,
} from '@littlebird/axon-mcp';

const Commands = {
  GetServers: 'axon-mcp:get-servers',
  GetReservedSlugs: 'axon-mcp:get-reserved-slugs',
  AddServer: 'axon-mcp:add-server',
  UpdateServer: 'axon-mcp:update-server',
  RemoveServer: 'axon-mcp:remove-server',
  TestServer: 'axon-mcp:test-server',
  EnsureBundled: 'axon-mcp:ensure-bundled',
  RespawnServer: 'axon-mcp:respawn-server',
  RunSetup: 'axon-mcp:run-setup',
  ListMailAccounts: 'axon-mcp:list-mail-accounts',
  GetNotesStats: 'axon-mcp:get-notes-stats',
  ListCalendars: 'axon-mcp:list-calendars',
  CheckOutlookSignIn: 'axon-mcp:check-outlook-sign-in',
  GetMeetingsOff: 'apple-calendar:get-meetings-off',
  SetMeetingsOff: 'apple-calendar:set-meetings-off',
  GetSetupProgress: 'axon-mcp:get-setup-progress',
  CheckLocalPaths: 'axon-mcp:check-local-paths',
  PickDirectory: 'axon-mcp:pick-directory',
  PickFile: 'axon-mcp:pick-file',
  SetupProgress: SETUP_PROGRESS_CHANNEL,
  DeviceCodePrompt: DEVICE_CODE_PROMPT_CHANNEL,
  DownloadProgress: RUNTIME_DOWNLOAD_PROGRESS_CHANNEL,
} as const;

export class AxonMcpAPI extends ElectronAPI {
  getServers(): Promise<ServerEntry[]> {
    return this.invoke<ServerEntry[]>(Commands.GetServers);
  }

  /** Built-in toolkit ids a server slug may not shadow (for truthful slug previews). */
  getReservedSlugs(): Promise<string[]> {
    return this.invoke<string[]>(Commands.GetReservedSlugs);
  }

  addServer(input: ServerEntryInput): Promise<ServerEntry[]> {
    return this.invoke<ServerEntry[]>(Commands.AddServer, input);
  }

  updateServer(
    slug: string,
    changes: Partial<ServerEntryInput> & { enabled?: boolean }
  ): Promise<ServerEntry[]> {
    return this.invoke<ServerEntry[]>(Commands.UpdateServer, {
      slug,
      changes,
    });
  }

  removeServer(slug: string): Promise<ServerEntry[]> {
    return this.invoke<ServerEntry[]>(Commands.RemoveServer, { slug });
  }

  /** `templateId` lets main gate flag-disabled templates regardless of transport */
  testServer(
    transport: ServerTransport,
    name?: string,
    templateId?: string
  ): Promise<ConnectionTestResult> {
    return this.invoke<ConnectionTestResult>(Commands.TestServer, {
      transport,
      name,
      templateId,
    });
  }

  ensureBundled(serverId: string): Promise<void> {
    return this.invoke<void>(Commands.EnsureBundled, { serverId });
  }

  respawnServer(slug: string): Promise<void> {
    return this.invoke<void>(Commands.RespawnServer, { slug });
  }

  runSetup(slug: string): Promise<void> {
    return this.invoke<void>(Commands.RunSetup, { slug });
  }

  /** Runs the Apple Mail server once to list the accounts on this Mac; may launch Mail */
  listMailAccounts(slug: string): Promise<MailAccountsResult> {
    return this.invoke<MailAccountsResult>(Commands.ListMailAccounts, { slug });
  }

  /** Calls the Apple Notes server's `get-notes-stats`; returns its `structuredContent` */
  getNotesStats(slug: string): Promise<unknown> {
    return this.invoke<unknown>(Commands.GetNotesStats, { slug });
  }

  /** Runs the Apple Calendar server once to list the calendars on this Mac */
  listCalendars(slug: string): Promise<CalendarListResult> {
    return this.invoke<CalendarListResult>(Commands.ListCalendars, { slug });
  }

  /** Starts the Outlook server's Microsoft sign-in if it hasn't happened, and reports where it stands */
  checkOutlookSignIn(slug: string): Promise<OutlookSignInState> {
    return this.invoke<OutlookSignInState>(Commands.CheckOutlookSignIn, {
      slug,
    });
  }

  getMeetingsOff(): Promise<string[]> {
    return this.invoke<string[]>(Commands.GetMeetingsOff);
  }

  setMeetingsOff(ids: string[]): Promise<void> {
    return this.invoke<void>(Commands.SetMeetingsOff, { ids });
  }

  getSetupProgress(): Promise<Record<string, string>> {
    return this.invoke<Record<string, string>>(Commands.GetSetupProgress);
  }

  checkLocalPaths(paths: readonly string[]): Promise<LocalPathCheckResult> {
    return this.invoke<LocalPathCheckResult>(Commands.CheckLocalPaths, {
      paths: [...paths],
    });
  }

  async onSetupProgress(
    callback: (event: SetupProgressEvent) => void
  ): Promise<ElectronUnsubscribe> {
    return this.addListener<SetupProgressEvent>({
      eventType: Commands.SetupProgress,
      callback,
    });
  }

  onDeviceCodePrompt(
    callback: (challenge: DeviceCodeSignInPrompt) => void
  ): ElectronUnsubscribe {
    return this.addListener<DeviceCodeSignInPrompt>({
      eventType: Commands.DeviceCodePrompt,
      callback,
    });
  }

  /** Install progress for every bundled server; callers filter by `serverId`. */
  onDownloadProgress(
    callback: (event: RuntimeDownloadProgress) => void
  ): ElectronUnsubscribe {
    return this.addListener<RuntimeDownloadProgress>({
      eventType: Commands.DownloadProgress,
      callback,
    });
  }

  pickDirectory(): Promise<string | null> {
    return this.invoke<string | null>(Commands.PickDirectory);
  }

  pickFile(): Promise<string | null> {
    return this.invoke<string | null>(Commands.PickFile);
  }
}
